import { Table, Spin, Button, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { useOperatorStore } from '../../store/useOperatorStore';
import { useOCPPOldLogs } from '../../Hooks/Index';
import { useDownloadPresignedUrl } from '../report/Hook/Index';
import { useState, useEffect } from 'react';
import dayjs from 'dayjs'; // Import dayjs for date formatting
import customParseFormat from 'dayjs/plugin/customParseFormat'; // Import custom parse format plugin
dayjs.extend(customParseFormat); // Extend dayjs with the plugin
import {
  DebouncedSelect,
  fetchChargerList,
} from '../../components/useDebouncedSelectLocation';
import WarningMessage from '../../components/WarningMessage';
import PropTypes from 'prop-types';

const ArchivedLogs = ({ charger: sharedCharger, operatorId: operatorIdProp }) => {
  const { t } = useTranslation('Ocpp-logs');
  const { operatorId: storeOperatorId } = useOperatorStore();
  const operatorId = operatorIdProp ?? storeOperatorId;
  const [charger, setCharger] = useState(sharedCharger ?? null);
  const [currentPage, setCurrentPage] = useState(1);

  // Keep archived tab aligned with parent (e.g. navigation via location.state) and when parent clears
  useEffect(() => {
    setCharger(sharedCharger ?? null);
    setCurrentPage(1);
  }, [sharedCharger?.value, sharedCharger?.label]);

  const { data, isLoading, error } = useOCPPOldLogs(
    operatorId,
    charger?.value,
    currentPage - 1
  );
  const { mutate: downloadFile, isLoading: isDownloading } =
    useDownloadPresignedUrl({
      onSuccess: (data) => {
        const downloadUrl = data.data;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', 'ArchivedLog.txt'); // Force download
        link.style.display = 'none'; // Hide the link
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link); // Clean up
      },
      onError: () => {
        console.error('Failed to download the report.');
      },
    });

  const columns = [
    {
      title: t('From'),
      dataIndex: 'form',
      key: 'form',
      render: (text) =>
        dayjs(text, 'D/M/YYYY, h:mm:ss a').isValid() // Adjust format to match your date strings
          ? dayjs(text, 'D/M/YYYY, h:mm:ss a').format('DD MMM YYYY, HH:mm')
          : 'Invalid Date',
    },
    {
      title: t('To'),
      dataIndex: 'to',
      key: 'to',
      render: (text) =>
        dayjs(text, 'D/M/YYYY, h:mm:ss a').isValid()
          ? dayjs(text, 'D/M/YYYY, h:mm:ss a').format('DD MMM YYYY, HH:mm')
          : 'Invalid Date',
    },
    {
      title: t('createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) =>
        dayjs(Number(text) * 1000).isValid()
          ? dayjs(Number(text) * 1000).format('DD MMM YYYY, HH:mm')
          : 'Invalid Date',
    },
    {
      title: t('Actions'),
      key: 'actions',
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => downloadFile({ fileUrl: record.s3Path })}
          loading={isDownloading}
        >
          {t('Download')}
        </Button>
      ),
    },
  ];

  if (!operatorId) {
    return <WarningMessage />;
  }

  return (
    <>
      <div className="mb-4">
        <DebouncedSelect
          id="chargerSelect"
          placeholder={t('searchCharger')}
          fetchOptions={(search, page) =>
            fetchChargerList({ operatorId, search, page })
          }
          onChange={(value) => setCharger(value)}
          value={charger}
          allowClear
          preload={Boolean(sharedCharger)}
          className="w-[250px]"
        />
      </div>
      {!charger && (
        <div className="flex justify-center items-center py-2 text-gray-500 font-medium">
          ⚠️ {t('selectChargerToViewLogs')}
        </div>
      )}
      {isLoading ? (
        <div className="flex justify-center items-center py-6">
          <Spin size="small" tip={t('loadingLogs')} />
        </div>
      ) : error ? (
        <div className="text-red-500 text-center py-4">
          {error?.response?.data?.message || t('errorLoadingLogs')}
        </div>
      ) : (
        charger && (
          <Table
            columns={columns}
            dataSource={data?.paginatedData || []}
            pagination={{
              total: data?.totalCount || 0,
              pageSize: 10,
              showSizeChanger: false,
              current: currentPage,
              onChange: (page) => setCurrentPage(page),
              position: ['bottomCenter'],
            }}
            locale={{
              emptyText: (
                <Empty
                  description={
                    charger ? 'No Data' : 'Please select a charger'
                  }
                />
              ),
            }}
            size="small"
            variant="outlined"
            className="w-full mt-4"
            rowKey="_id"
          />
        )
      )}
    </>
  );
};

ArchivedLogs.propTypes = {
  charger: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  operatorId: PropTypes.string,
};

export default ArchivedLogs;
